import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DetailSection from '@/src/components/organism/DetailSection';
import {
  getAdministratorByIdAPI,
  putPasswordResetAPI,
} from '@/src/apis/rest/administrators';
import { formatter } from '@/src/utils/formatter';
import { useToast } from '@/hooks/useToast';
import { useCallback } from 'react';
import useDialog, { DialogType } from '@/hooks/useDialog';
import { DetailSctionRenderer } from '@/components/organism/DetailSection/renderer';
import { OPTIONS_CONST } from '@/src/constants/options';

const BACK_PATH = PATH.ADMINISTRATOR_LIST;
const EDIT_PATH = PATH.ADMINISTRATOR_EDIT;

function AdministratorDetailPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();
  const { Dialog, isOpen, openDialog, closeDialog } = useDialog({
    type: DialogType.ALERT,
  });

  const { data: administratorData } = useQuery({
    queryKey: ['administrator', id],
    queryFn: () => getAdministratorByIdAPI(Number(id)),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  const putResetPassword = useMutation({
    mutationFn: (id: string) => putPasswordResetAPI(Number(id)),
  });

  const handleResetPassword = useCallback(async () => {
    try {
      await putResetPassword.mutateAsync(id!);

      toast({
        title: '비밀번호 초기화 성공',
        description: '기본 비밀번호로 초기화 되었습니다.',
        variant: 'success',
      });
    } catch (e) {
      toast({
        title: '비밀번호 초기화 실패',
        description:
          '비밀번호 초기화에 실패하였습니다. 시스템 관리자에게 문의하세요.',
        variant: 'error',
      });
    }
  }, [toast, id, putResetPassword]);

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Link to={BACK_PATH}>
            <Button className="w-max" buttonSize="medium" buttonColor="outline">
              <div className="flex items-center gap-x-2">
                <LucideIcon name="ChevronLeft" size={16} />
                {BUTTON_CONST.backToScreen}
              </div>
            </Button>
          </Link>
          <Link to={EDIT_PATH.replace(':id', id || '')}>
            <Button className="w-40" buttonSize="medium">
              {BUTTON_CONST.editShort}
            </Button>
          </Link>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={closeDialog}
        onOk={handleResetPassword}
        okButtonProps={{
          isLoading: putResetPassword.isPending,
        }}
        title="비밀번호 초기화"
      >
        {'비밀번호를 초기화 하시겠습니까?\n기본 비밀번호는 ‘ecoya1q2w3e!@’ 입니다.'
          .split('\n')
          .map(line => (
            <>
              {line}
              <br />
            </>
          ))}
      </Dialog>
      <MainContentTitle
        breadcrumbList={['관리자 관리', '관리자 상세']}
        pageTitle="관리자 상세"
      />

      <DetailSection
        sectionTitle={
          <div className="flex w-full items-center justify-between">
            <span className="font-pretendardSemiBold text-b18sb">
              관리자 정보
            </span>
          </div>
        }
        data={administratorData}
        contents={[
          [
            {
              key: 'aId',
              lable: '아이디 (Email)',
              valueSetter: data => data?.aId || '-',
              render: data => {
                return (
                  <>
                    <span>{data}</span>
                    <Button
                      buttonColor="secondary"
                      buttonSize="xSmall"
                      onClick={openDialog}
                    >
                      비밀번호 초기화
                    </Button>
                  </>
                );
              },
            },
            {
              key: 'name',
              lable: '이름',
              valueSetter: data => data?.name || '-',
            },
            {
              key: 'department',
              lable: '소속',
              valueSetter: data => data?.department || '-',
            },
          ],
          [
            {
              key: 'phone',
              lable: '핸드폰 번호',
              valueSetter: data =>
                data?.phone ? formatter.PHONE(data.phone) : '-',
            },
            {
              key: 'isActivated',
              lable: '사용 여부',
              valueSetter: data =>
                data?.account?.isActivated ? 'true' : 'false',
              render: DetailSctionRenderer.BADGE({
                options: [
                  {
                    label: OPTIONS_CONST.activated,
                    key: 'true',
                    badgeColor: 'lightGreen',
                  },
                  {
                    label: OPTIONS_CONST.notActivated,
                    key: 'false',
                    badgeColor: 'lightGray',
                  },
                ],
              }),
            },
            {
              key: 'createdAt',
              lable: '등록 일시',
              render: DetailSctionRenderer.DATE({
                format: 'YYYY-MM-DD HH:mm:ss',
              }),
            },
          ],
        ]}
      />
      {renderBottomFixedContent()}
    </>
  );
}

export default AdministratorDetailPage;
